/**
 * Estado de sesión del dashboard.
 *
 * Qué expone y qué no:
 *
 * - **Expone** los claims del token (rol, tenant, nombre) para pintar la UI,
 *   y las acciones `login` / `logout`.
 * - **No expone** los tokens. Nadie fuera de `ApiClient` los necesita, y una
 *   vista que pueda leerlos es una vista que puede loguearlos.
 *
 * Arranque: el estado sale de lo que haya en el store, de forma síncrona. No
 * hay un `useEffect` que "cargue la sesión" ni una pantalla intermedia — con
 * `localStorage` la lectura es síncrona, y meter un estado `loading` sólo
 * agregaría un parpadeo entre el login y el dashboard.
 *
 * `GET /auth/me` NO se llama al arrancar: los claims ya vienen firmados
 * dentro del token y consultarlos costaría un round-trip antes del primer
 * pintado. Se usa en el módulo de administración, donde interesa el dato de
 * base (por ejemplo `is_active`), no el del token.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { SessionEndReason } from '@/api/client';
import { asApiError, type ApiError } from '@/api/errors';
import type { Role } from '@/api/types';
import { useApi } from '@/api/useApi';
import { decodeAccessClaims } from './claims';
import { hasRole } from './roles';
import { sessionFromPair, type TokenPairResponse, type TokenStore } from './tokens';
import { AuthContext, type AuthValue, type LoginInput } from './useAuth';

export interface AuthProviderProps {
  tokens: TokenStore;
  children: ReactNode;
  /**
   * Se llama cuando el `ApiClient` da la sesión por terminada. El
   * `AppProviders` lo conecta con `setSessionEnded` del propio provider;
   * existe como prop para poder probarlo sin montar la app entera.
   */
  registerSessionEndHandler?: (
    handler: (reason: SessionEndReason, error: ApiError | null) => void,
  ) => () => void;
}

export function AuthProvider({
  tokens,
  children,
  registerSessionEndHandler,
}: AuthProviderProps): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const [accessToken, setAccessToken] = useState<string | null>(
    () => tokens.read()?.accessToken ?? null,
  );
  const [endedReason, setEndedReason] = useState<SessionEndReason | null>(null);

  // Otra pestaña inició o cerró sesión: esta se alinea. Sin esto, cerrar
  // sesión en una pestaña deja la otra mostrando datos con un token muerto.
  useEffect(
    () =>
      tokens.subscribe((session) => {
        setAccessToken(session?.accessToken ?? null);
        if (session === null) queryClient.clear();
      }),
    [tokens, queryClient],
  );

  useEffect(() => {
    if (registerSessionEndHandler === undefined) return undefined;
    return registerSessionEndHandler((reason) => {
      setAccessToken(null);
      setEndedReason(reason);
      queryClient.clear();
    });
  }, [registerSessionEndHandler, queryClient]);

  const claims = useMemo(
    () => (accessToken === null ? null : decodeAccessClaims(accessToken)),
    [accessToken],
  );

  const login = useCallback(
    async (input: LoginInput) => {
      const pair = await api.post<TokenPairResponse>('/api/v1/auth/login', input, {
        auth: false,
      });
      // La cache anterior puede tener datos de OTRO tenant si alguien cambia
      // de marca en la misma pestaña. Se limpia antes de mostrar nada.
      queryClient.clear();
      tokens.write(sessionFromPair(pair, Date.now()));
      setAccessToken(pair.access_token);
      setEndedReason(null);
    },
    [api, tokens, queryClient],
  );

  const logout = useCallback(
    async (options: { allSessions?: boolean } = {}) => {
      const session = tokens.read();
      if (session !== null) {
        try {
          await api.post<unknown>(
            '/api/v1/auth/logout',
            {
              refresh_token: session.refreshToken,
              all_sessions: options.allSessions ?? false,
            },
            { auth: false },
          );
        } catch (error) {
          // Que la API no conteste no puede dejar al usuario dentro del
          // dashboard: la sesión local se cierra igual. El refresh queda
          // vivo del lado del servidor hasta que expire; es el compromiso
          // aceptable frente a un botón de "cerrar sesión" que no cierra.
          if (asApiError(error).status >= 500) {
            console.warn('logout: la API no confirmó la revocación');
          }
        }
      }
      api.endSession('logout');
      setAccessToken(null);
      setEndedReason(null);
      queryClient.clear();
    },
    [api, tokens, queryClient],
  );

  const value = useMemo<AuthValue>(
    () => ({
      claims,
      isAuthenticated: claims !== null,
      endedReason,
      can: (minimum: Role) => hasRole(claims?.role, minimum),
      login,
      logout,
    }),
    [claims, endedReason, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
